/**
 * this MessageNotification react component that will render a link from Amplience. This should utilize the existing link_huge
 *
 * @module views/components/MessageNotification
 * @memberof -Common
 */
import './MessageNotification.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { MessageNotification as PalMessageNotification } from 'web-palette/dist/components/MessageNotification/MessageNotification';

import Button from '@ulta/core/components/Button/Button';
import GridContainer from '@ulta/core/components/GridContainer/GridContainer';
import { useIntersectionProcessor } from '@ulta/core/hooks/useIntersectionProcessor/useIntersectionProcessor';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { getUltaAssetHtml } from '@ulta/core/utils/getUltaAssetHtml/getUltaAssetHtml';

import { constants } from '@ulta/utils/constants/constants';
import interpolateString from '@ulta/utils/interpolateString/interpolateString';

import * as utils from './MessageNotification';

const { COLUMN_CONTAINER_BREAKPOINTS } = constants;

/**
 * Represents a MessageNotification component
 *
 * @method
 * @param {MessageNotificationProps} props - React properties passed from composition
 * @returns MessageNotification
 */
export const MessageNotification = function( props ){
  const {
    title,
    variantsLabel,
    showLess,
    showMore,
    maxItemDisplay,
    variants,
    setShowSpacer,
    thumbnailImage
  } = props;
  const { breakpoint } = useDeviceInflection();
  const CURRENT_BREAKPOINT = COLUMN_CONTAINER_BREAKPOINTS[breakpoint.CURRENT_BREAKPOINT];
  const sflPaginationlimit = maxItemDisplay?.[CURRENT_BREAKPOINT] ?
    maxItemDisplay[CURRENT_BREAKPOINT] :
    maxItemDisplay?.mobile;
  const [displayCount, setDisplayCount] = useState( sflPaginationlimit );
  const variantsLength = variants?.length;
  const isButtonVisible = variantsLength > sflPaginationlimit;
  const [isMaxCountVisible, setIsMaxCountVisible] = useState( displayCount !== variantsLength );
  const componentRef = useRef();

  useEffect( () => {
    setDisplayCount( sflPaginationlimit );
  }, [sflPaginationlimit] );

  useEffect( () => {
    setIsMaxCountVisible( displayCount !== variantsLength );
  }, [displayCount] );

  useEffect( () => {
    if( setShowSpacer ){
      setShowSpacer( !!title );
    }
  }, [title] );

  let items = variants?.slice( 0, displayCount );
  const buttonLabel = isMaxCountVisible ? interpolateString( showMore?.label, [variants?.length - sflPaginationlimit] ) : showLess?.label;

  const handleToggleShowMore = useCallback(
    utils.composeToggleShowMore(
      { isMaxCountVisible, variantCount: variantsLength, sflPaginationlimit },
      { setDisplayCount, setIsMaxCountVisible }
    ),
    [isMaxCountVisible, displayCount, setIsMaxCountVisible, variants, sflPaginationlimit]
  );

  useIntersectionProcessor( {
    ...props,
    componentRef
  } );

  if( !title ){
    return null;
  }

  const formattedThumbnail = thumbnailImage ? getUltaAssetHtml( { thumbnailImage: thumbnailImage } ) : null;

  const externalButtonElement = ( isButtonVisible ?
    ( <div className='MessageNotification__button'>
      <Button
        likeLink
        onClick={ handleToggleShowMore }
        label={ buttonLabel }
        size={ 'compact' }
      />
    </div> ) : null
  );

  return (
    <div
      className='MessageNotification'
      ref={ componentRef }
    >
      <GridContainer>
        <div className='MessageNotification__wrapper'>
          <PalMessageNotification
            message={ title }
            variantTypeLabel={ variantsLabel }
            variants={ items }
            externalActionElement={ externalButtonElement }
            externalMediaElement={ formattedThumbnail }
          />
        </div>

      </GridContainer>
    </div>
  );
};

/**
   * Method to handle action.handletoggleShowMore callback for show more/less buttons
   * @param {object} data data
   * @param {action} data.isMaxCountVisible data to validate isMaxCountVisible
   * @param {action} data.maxItemDisplay data to show maxItemDisplay
   * @param {action} data.variantCount data to show variantCount
   * @param {object} methods methods
   * @param {action} methods.setDisplayCount method to setDisplayCount
   * @param {action} methods.setIsMaxCountVisible method to setIsMaxCountVisible
   */
export const composeToggleShowMore = ( data, methods ) => ( ) => {
  const { isMaxCountVisible, sflPaginationlimit, variantCount } = data || {};
  const { setIsMaxCountVisible, setDisplayCount } = methods || {};

  if( isMaxCountVisible ){
    setDisplayCount( variantCount );
    setIsMaxCountVisible( false );
  }
  else {
    setDisplayCount( sflPaginationlimit );
    setIsMaxCountVisible( true );
  }
};

/**
 * Property type definitions
 * @typedef MessageNotificationProps
 * @type {object}
 * @property {string} title - Title of the Last Purchased Date ex: Previously ordered Jan 21, 2023.
 * @property {string} variantsLabel - Label of the variants ex: Color
 * @property {object} showLess - Sets the label for show less button.
 * @property {object} showMore - Sets the label for show more button.
 * @property {object} maxItemDisplay - Sets the number of variants to display on the intial load.
 * @property {array} vaiants - variants list in the array format
 * @property {object} thumbnailImage - thumbnail image
 */

export const propTypes = {
  title: PropTypes.string.isRequired,
  variantsLabel: PropTypes.string.isRequired,
  showLess: PropTypes.object.isRequired,
  showMore: PropTypes.object.isRequired,
  showMoreLabel: PropTypes.string.isRequired,
  maxItemDisplay: PropTypes.object.isRequired,
  variants: PropTypes.arrayOf(
    PropTypes.shape( {
      label: PropTypes.string,
      image: PropTypes.object
    } )
  ).isRequired,
  thumbnailImage: PropTypes.object
};

MessageNotification.propTypes = propTypes;

export default MessageNotification;